import type { ReferrerSummary } from "@redotech/admin-sdk/rpc/schema/referrers/shared";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextareaInputField } from "@redotech/arbiter/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import classNames from "classnames";
import { memo, useCallback, useState } from "react";

import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
// Reuse the create-referrer modal shell for consistent admin actions.
import * as modalStyles from "../../onboardings/workspace/v2-product-configurations.module.css";

interface ImpersonateReferrerButtonProps {
  referrer: ReferrerSummary;
}

const REASON_MAX_LEN = 500;

// Same image as the create-referrer modal — keeps the referrers admin
// surface visually consistent. Swap to a referrers-specific asset when
// product gives one.
const IMPERSONATION_MODAL_IMAGE_URL =
  "https://redoapi-dev.s3.us-east-1.amazonaws.com/redo/static/onboarding-v2/onboarding-v2-returns.webp";

/**
 * "Open dashboard" — surfaces the admin → referrer impersonation flow on the
 * referrer detail page. Mints a handoff URL via `impersonateReferrer`, opens
 * the referrals portal in a new tab. The reason field is required and stored
 * on the audit row.
 */
export const ImpersonateReferrerButton = memo(
  function ImpersonateReferrerButton({
    referrer,
  }: ImpersonateReferrerButtonProps) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const isActive = referrer.status === "active";

    // Clamp here rather than passing maxLength to RedoTextareaInputField,
    // since that component renders an i18n-broken built-in counter when
    // maxLength is set.
    const handleReasonChange = useCallback((next: string) => {
      setReason(
        next.length > REASON_MAX_LEN ? next.slice(0, REASON_MAX_LEN) : next,
      );
    }, []);

    const handleClose = useCallback(() => {
      if (submitting) {
        return;
      }
      setOpen(false);
      setReason("");
    }, [submitting]);

    const submit = useCallback(async () => {
      const trimmed = reason.trim();
      if (trimmed.length === 0) {
        toast("Please enter a reason for impersonation.", { variant: "error" });
        return;
      }
      setSubmitting(true);
      try {
        const { handoffUrl } = await rpcClient.impersonateReferrer({
          referrerId: referrer.id,
          reason: trimmed,
        });
        // noopener/noreferrer prevents the new tab from accessing
        // window.opener and from leaking the admin URL as Referer.
        window.open(handoffUrl, "_blank", "noopener,noreferrer");
        toast("Opening referrer dashboard in a new tab.", {
          variant: "success",
        });
        setOpen(false);
        setReason("");
      } catch (err) {
        toast(
          err instanceof Error
            ? err.message
            : "Failed to start impersonation. Try again.",
          { variant: "error" },
        );
      } finally {
        setSubmitting(false);
      }
    }, [reason, rpcClient, referrer.id]);

    const submitDisabled = submitting || reason.trim().length === 0;
    const tooltipText = !isActive
      ? `Cannot impersonate a ${referrer.status} referrer.`
      : undefined;

    return (
      <>
        <RedoButton
          disabled={!isActive || submitting}
          hierarchy="secondary"
          onClick={() => setOpen(true)}
          size="sm"
          text="Open dashboard"
          title={tooltipText}
        />
        {open && (
          <RedoModal
            hideFooter
            hideHeader
            isOpen={open}
            modalSize="sm"
            muiClassName={modalStyles.exitModalRoot}
            noPadding
            onModalCloseRequested={handleClose}
            title={`Impersonate ${referrer.name}`}
          >
            <div className={modalStyles.modalImageWrapper}>
              <img
                alt=""
                className={modalStyles.modalHeaderImage}
                src={IMPERSONATION_MODAL_IMAGE_URL}
              />
              <div className={modalStyles.modalImageOverlay} />
              <div className={modalStyles.modalImageLabel}>
                <Text
                  as="span"
                  className={modalStyles.modalImageTitle}
                  lineHeight="1"
                >
                  Impersonate {referrer.name}
                </Text>
                <Text as="p" className={modalStyles.modalImageSubheading}>
                  Your admin email and the reason below are recorded on every
                  authenticated request.
                </Text>
              </div>
            </div>

            <div className={modalStyles.modalContentBody}>
              <Flex dir="column" gap="md">
                <RedoTextareaInputField
                  disabled={submitting}
                  label="Reason"
                  placeholder="e.g. Investigating ticket #1234: referrer reports missing installs."
                  setValue={handleReasonChange}
                  value={reason}
                />
                <Text fontSize="xs" textColor="secondary">
                  {reason.length}/{REASON_MAX_LEN} characters
                </Text>
              </Flex>

              <div className={modalStyles.actionsRow}>
                <button
                  className={modalStyles.exitModalActionBtn}
                  disabled={submitting}
                  onClick={handleClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={classNames(
                    modalStyles.exitModalActionBtn,
                    modalStyles.exitModalActionBtnPrimary,
                    modalStyles.primaryActionBtn,
                  )}
                  disabled={submitDisabled}
                  onClick={() => void submit()}
                  type="button"
                >
                  <Text
                    as="span"
                    className={classNames(
                      modalStyles.primaryActionLabel,
                      submitting && modalStyles.primaryActionLabelHidden,
                    )}
                  >
                    {submitting ? "Opening…" : "Open dashboard"}
                  </Text>
                  {submitting && (
                    <span
                      aria-label="Opening"
                      className={modalStyles.primaryActionSpinner}
                      role="status"
                    />
                  )}
                </button>
              </div>
            </div>
          </RedoModal>
        )}
      </>
    );
  },
);
